import { Check, Ban, PauseCircle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { JobStage, StageStrip as StageStripData } from "../jobDetailHelpers";

function shortStamp(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const TERMINAL_CONFIG = {
    cancelled: {
        label: "Cancelled",
        Icon: Ban,
        cls: "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300",
    },
    held: {
        label: "On hold",
        Icon: PauseCircle,
        cls: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
    },
    cannot_complete: {
        label: "Cannot complete",
        Icon: AlertTriangle,
        cls: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
    },
} as const;

/**
 * Horizontal stage tracker under the header (spec §3.2). Derived data only —
 * clicking a stage scrolls to the card holding its evidence.
 */
export function StageStrip({
    strip,
    onStageClick,
}: {
    strip: StageStripData;
    onStageClick?: (key: JobStage["key"]) => void;
}) {
    const terminal = strip.terminal ? TERMINAL_CONFIG[strip.terminal] : null;
    return (
        <div className="flex flex-wrap items-center gap-y-1 py-1.5" role="list" aria-label="Job stages">
            {strip.stages.map((stage, idx) => {
                // #825 M6: a current stage with NO evidence yet (no timestamp)
                // is a destination, not a fact — an awaiting-scheduling job's
                // strip used to fill "Scheduled" exactly like an achieved
                // stage while the body said "Not scheduled yet". Render it as
                // an outlined "Next:" pill so current-but-not-done can never
                // read as done. A live stage ("Testing now") is happening, not
                // upcoming — it fills, with no date.
                const upcoming = stage.state === "current" && !stage.ts && !stage.live;
                return (
                    <div key={stage.key} className="flex items-center shrink-0" role="listitem">
                        {idx > 0 && <div className="mx-1 h-px w-3 bg-border" aria-hidden />}
                        <button
                            type="button"
                            onClick={() => onStageClick?.(stage.key)}
                            className={cn(
                                "flex items-center gap-1.5 rounded-full border leading-none transition-colors",
                                // The active stage is the answer to "where does this
                                // job stand?" — render it ~1.2x and filled so it pops
                                // off the strip at a glance (spec §6b.4).
                                stage.state === "current" && upcoming
                                    ? "border-primary border-dashed bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary shadow-sm"
                                    : stage.state === "current"
                                      ? "border-primary bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground shadow-sm"
                                      : stage.state === "done"
                                        ? "border-transparent bg-muted/60 px-2.5 py-1 text-[11px] text-foreground/80 hover:bg-muted"
                                        : "border-dashed px-2.5 py-1 text-[11px] text-foreground/40 hover:text-foreground/60",
                            )}
                            title={
                                upcoming
                                    ? `${stage.label} hasn't happened yet — it's the step this job is working toward`
                                    : stage.ts
                                      ? new Date(stage.ts).toLocaleString()
                                      : undefined
                            }
                        >
                            {stage.state === "done" && <Check className="h-3 w-3 text-emerald-600" />}
                            {stage.state === "current" && !upcoming && (
                                <span className="h-2 w-2 rounded-full bg-primary-foreground" aria-hidden />
                            )}
                            <span>
                                {upcoming ? "Next: " : ""}
                                {stage.label}
                            </span>
                            {stage.ts && stage.state !== "pending" && (
                                <span
                                    className={cn(
                                        "text-[10px] font-normal",
                                        stage.state === "current"
                                            ? "text-primary-foreground/80"
                                            : "text-muted-foreground",
                                    )}
                                >
                                    {shortStamp(stage.ts)}
                                </span>
                            )}
                            {stage.note && (
                                <span
                                    className={cn(
                                        "text-[10px] font-normal",
                                        stage.state === "current" && !upcoming
                                            ? "text-primary-foreground/80"
                                            : "text-amber-600 dark:text-amber-400",
                                    )}
                                >
                                    {stage.note}
                                </span>
                            )}
                        </button>
                    </div>
                );
            })}
            {terminal && (
                <div className="ml-2 shrink-0">
                    <span
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold leading-none",
                            terminal.cls,
                        )}
                    >
                        <terminal.Icon className="h-3 w-3" />
                        {terminal.label}
                    </span>
                </div>
            )}
        </div>
    );
}
